// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\forms\Form4Products.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../../components/MerchantLayout";
import { apiGet, apiPut } from "../../api";

type Form4 = {
  // 特定継続的役務
  continuous_rate: string; // ○%
  continuous_max_days: string; // ○日
  continuous_treatment_name: string; // 具体的な施術名

  // 特定継続的役務外
  non_continuous_rate: string; // ○%
  non_continuous_max_days_after_contract: string; // 契約日より○日後
  non_continuous_treatment_name: string; // 具体的な施術名

  // 商品
  goods_rate: string; // ○%
  supplier: string; // 仕入れ先
  goods_type: string; // 商品種類

  // BtoB
  b2b_goods: string; // 取扱商品
  b2b_delivery_days_after_contract: string; // 契約日より○日後

  // 価格帯（必須）: dropdown + free input (only when "その他（自由入力）")
  price_range_select: string;
  price_range_text: string;

  is_completed: boolean;
};

const empty: Form4 = {
  continuous_rate: "",
  continuous_max_days: "",
  continuous_treatment_name: "",

  non_continuous_rate: "",
  non_continuous_max_days_after_contract: "",
  non_continuous_treatment_name: "",

  goods_rate: "",
  supplier: "",
  goods_type: "",

  b2b_goods: "",
  b2b_delivery_days_after_contract: "",

  price_range_select: "",
  price_range_text: "",

  is_completed: false,
};

const PRICE_OPTIONS = [
  "〜1万円",
  "1万円〜5万円",
  "5万円〜10万円",
  "10万円〜30万円",
  "30万円〜50万円",
  "50万円〜100万円",
  "100万円以上",
  "その他（自由入力）",
];

// ✅ 業種ラベル（staff createの固定値）
const INDUSTRY_OPTIONS = ["美容医療", "エステティック", "異業種", "BtoB", "その他"] as const;
type Industry = (typeof INDUSTRY_OPTIONS)[number];

function normalizeIndustry(rawIndustry: any, rawBusinessType: any): Industry {
  const ind = String(rawIndustry || "").trim();
  if (INDUSTRY_OPTIONS.includes(ind as Industry)) return ind as Industry;

  // business_type is sometimes free text for 異業種/その他, so only use it if it matches fixed labels.
  const bt = String(rawBusinessType || "").trim();
  if (INDUSTRY_OPTIONS.includes(bt as Industry)) return bt as Industry;

  // fallback
  return "その他";
}

export default function Form4Products() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState<Form4>(empty);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ 業種（adminが作成時に決める）
  const [industry, setIndustry] = useState<Industry>("その他");

  // ✅ Visibility rules (Page 12/13)
  const isBtoB = industry === "BtoB";

  // Page12: “特定継続的役務外” is × only for エステティック
  const showNonContinuousBlock = !isBtoB && industry !== "エステティック";

  // Page12: these are shown for 美容医療/エステ/異業種/その他
  const showMainProductsSection = !isBtoB;

  // Page13: BtoB section only for BtoB
  const showBtoBSection = isBtoB;

  // ✅ Step③: Completion rule (visible-only required)
  // 「（必須）」が付いている項目だけで判定する
  const canComplete = useMemo(() => {
    // ----- BtoB -----
    if (isBtoB) {
      const b2bOk =
        data.b2b_goods.trim() !== "" &&
        data.b2b_delivery_days_after_contract.trim() !== "" &&
        data.price_range_select.trim() !== "";

      if (!b2bOk) return false;

      if (data.price_range_select === "その他（自由入力）") {
        return data.price_range_text.trim() !== "";
      }
      return true;
    }

    // ----- non-BtoB -----
    // 特定継続的役務（必須）
    const continuousOk =
      data.continuous_rate.trim() !== "" &&
      data.continuous_max_days.trim() !== "" &&
      data.continuous_treatment_name.trim() !== "";

    if (!continuousOk) return false;

    // 特定継続的役務外（表示されている場合のみ必須）
    if (showNonContinuousBlock) {
      const nonContinuousOk =
        data.non_continuous_rate.trim() !== "" &&
        data.non_continuous_max_days_after_contract.trim() !== "" &&
        data.non_continuous_treatment_name.trim() !== "";
      if (!nonContinuousOk) return false;
    }

    // 商品（必須）
    const goodsOk =
      data.goods_rate.trim() !== "" && data.supplier.trim() !== "" && data.goods_type.trim() !== "";
    if (!goodsOk) return false;

    // 価格帯は「（必須）」ではない（BtoBのみ必須）なので non-BtoB では判定に含めない
    return true;
  }, [data, isBtoB, showNonContinuousBlock]);

  const statusLabel = canComplete ? "入力済み" : "未完了";
  

  useEffect(() => {
    const run = async () => {
      setError("");
      setSuccess("");

      try {
        // ✅ 1) fetch merchant info for 業種
        const meJson = await apiGet("/merchant/me");
        const m = meJson?.merchant || {};
        const resolvedIndustry = normalizeIndustry(m?.industry, m?.business_type);
        setIndustry(resolvedIndustry);

        // ✅ 2) fetch existing form data
        const json = await apiGet("/merchant/forms/products");
        const d = json?.data;

        if (d) {
          setData({
            continuous_rate: d.continuous_rate != null ? String(d.continuous_rate) : "",
            continuous_max_days: d.continuous_max_days != null ? String(d.continuous_max_days) : "",
            continuous_treatment_name: d.continuous_treatment_name || "",

            non_continuous_rate: d.non_continuous_rate != null ? String(d.non_continuous_rate) : "",
            non_continuous_max_days_after_contract:
              d.non_continuous_max_days_after_contract != null
                ? String(d.non_continuous_max_days_after_contract)
                : "",
            non_continuous_treatment_name: d.non_continuous_treatment_name || "",

            goods_rate: d.goods_rate != null ? String(d.goods_rate) : "",
            supplier: d.supplier || "",
            goods_type: d.goods_type || "",

            b2b_goods: d.b2b_goods || "",
            b2b_delivery_days_after_contract:
              d.b2b_delivery_days_after_contract != null
                ? String(d.b2b_delivery_days_after_contract)
                : "",

            price_range_select: d.price_range_select || "",
            price_range_text: d.price_range_text || "",

            is_completed: Boolean(d.is_completed),
          });
        } else {
          setData(empty);
        }

        setLoading(false);
      } catch (e: any) {
        setError(
          e?.message ||
            "取扱商品の取得に失敗しました。バックエンドが起動しているか、Form4のAPIが実装済みか確認してください。"
        );
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const onSave = async (): Promise<boolean> => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const json = await apiPut("/merchant/forms/products", {
        continuous_rate: data.continuous_rate,
        continuous_max_days: data.continuous_max_days,
        continuous_treatment_name: data.continuous_treatment_name,

        non_continuous_rate: data.non_continuous_rate,
        non_continuous_max_days_after_contract: data.non_continuous_max_days_after_contract,
        non_continuous_treatment_name: data.non_continuous_treatment_name,

        goods_rate: data.goods_rate,
        supplier: data.supplier,
        goods_type: data.goods_type,

        b2b_goods: data.b2b_goods,
        b2b_delivery_days_after_contract: data.b2b_delivery_days_after_contract,

        price_range_select: data.price_range_select,
        price_range_text: data.price_range_select === "その他（自由入力）" ? data.price_range_text : "",

        is_completed: canComplete,
      });

      setData((prev) => ({ ...prev, is_completed: canComplete }));
      const okCompleted = Boolean(json?.is_completed ?? canComplete);
      setSuccess(okCompleted ? "保存しました（入力済み）" : "保存しました（未完了）");
      setSaving(false);
      return true;
    } catch (e: any) {
      setError(
        e?.message ||
          "保存に失敗しました。バックエンドが起動しているか、Form4のAPIが実装済みか確認してください。"
      );
      setSaving(false);
      return false;
    }
  };

    const onBack = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/payment-handling");
  };

  const onNext = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/sales-method");
  };

  const onList = async () => {
  const ok = await onSave();
  if (!ok) return;
  navigate("/merchant/register");
};





  const showPriceText = data.price_range_select === "その他（自由入力）";

  return (
    <MerchantLayout title="4. 取扱商品" active="forms">
      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: 12,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {success}（いつでも編集できます）
        </div>
      )}

      {!loading && (
        <>
          {/* ✅ small info row (non-editable) */}
          <div style={{ marginBottom: 10, display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ fontSize: 13, color: "#475569", fontWeight: 900 }}>業種：</div>
            <div
              style={{
                background: "#0b1f3b",
                color: "#fff",
                fontWeight: 900,
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 999,
              }}
            >
              {industry}
            </div>
          </div>

          {/* ✅ Main products section (Page 12) */}
          {showMainProductsSection && (
            <div
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                取扱商品（必須）
              </div>

              <div style={{ padding: 12 }}>
                {/* Industry header pills (display only) */}
                

                <RowBlock
                  title="特定継続的役務"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="特定継続的役務" required>
                      <SuffixInput
  value={data.continuous_rate}
  onChange={(value) => setData({ ...data, continuous_rate: value })}
  placeholder="例）30"
  suffix="%"
/>
                    </Field>
                  }
                  mid={
                    <Field label="最長役務期間" required>
                      <SuffixInput
  value={data.continuous_max_days}
  onChange={(value) => setData({ ...data, continuous_max_days: value })}
  placeholder="例）90"
  suffix="日"
/>
                    </Field>
                  }
                  right={
                    <Field label="具体的な施術名" required>
                      <input
                        value={data.continuous_treatment_name}
                        onChange={(e) =>
                          setData({ ...data, continuous_treatment_name: e.target.value })
                        }
                        placeholder="例）フェイシャル、痩身 など"
                        style={inputStyle}
                      />
                    </Field>
                  }
                />

                <div style={{ height: 10 }} />

                {/* ✅ 特定継続的役務外: hidden only for エステティック */}
                {showNonContinuousBlock && (
                  <>
                    <RowBlock
                      title="特定継続的役務外"
                      industryMarks={["○", "×", "○", "○"]}
                      left={
                        <Field label="特定継続的役務外" required>
                          <SuffixInput
  value={data.non_continuous_rate}
  onChange={(value) => setData({ ...data, non_continuous_rate: value })}
  placeholder="例）70"
  suffix="%"
/>
                        </Field>
                      }
                      mid={
                        <Field label="最長施術日程" required>
                          <SuffixInput
  value={data.non_continuous_max_days_after_contract}
  onChange={(value) =>
    setData({
      ...data,
      non_continuous_max_days_after_contract: value,
    })
  }
  placeholder="例）30"
  suffix="日後"
/>
                        </Field>
                      }
                      right={
                        <Field label="具体的な施術名" required>
                          <input
                            value={data.non_continuous_treatment_name}
                            onChange={(e) =>
                              setData({
                                ...data,
                                non_continuous_treatment_name: e.target.value,
                              })
                            }
                            placeholder="例）二重手術、外科手術 など"
                            style={inputStyle}
                          />
                        </Field>
                      }
                    />
                    <div style={{ height: 10 }} />
                  </>
                )}

                <RowBlock
                  title="商品"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="商品" required>
                      <SuffixInput
  value={data.goods_rate}
  onChange={(value) => setData({ ...data, goods_rate: value })}
  placeholder="例）20"
  suffix="%"
/>
                    </Field>
                  }
                  mid={
                    <Field label="仕入れ先" required>
                      <input
                        value={data.supplier}
                        onChange={(e) => setData({ ...data, supplier: e.target.value })}
                        placeholder="例）〇〇商事、メーカー名 など"
                        style={inputStyle}
                      />
                    </Field>
                  }
                  right={
                    <Field label="商品種類" required>
                      <input
                        value={data.goods_type}
                        onChange={(e) => setData({ ...data, goods_type: e.target.value })}
                        placeholder="例）化粧品、健康食品、機器 など"
                        style={inputStyle}
                      />
                    </Field>
                  }
                />
              </div>
            </div>
          )}

          {/* ✅ BtoB section (Page 13) */}
          {showBtoBSection && (
            <div
              style={{
                marginTop: 0,
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                BtoB（必須）
              </div>

              <div style={{ padding: 12 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
                    gap: 20,
                    alignItems: "start",
                  }}
                >
                  <Field label="取扱商品" required>
                    <input
                      value={data.b2b_goods}
                      onChange={(e) => setData({ ...data, b2b_goods: e.target.value })}
                      placeholder="例）業務用機器、卸商品 など"
                      style={inputStyle}
                    />
                  </Field>

                  <Field label="納品時期" required>
                    <SuffixInput
  value={data.b2b_delivery_days_after_contract}
  onChange={(value) =>
    setData({ ...data, b2b_delivery_days_after_contract: value })
  }
  placeholder="例）14"
  suffix="日後"
/>
                  </Field>

                  <div style={{ minWidth: 0 }}>
                    <Field label="価格帯" required>
                      <select
                        value={data.price_range_select}
                        onChange={(e) =>
                          setData({
                            ...data,
                            price_range_select: e.target.value,
                            price_range_text:
                              e.target.value === "その他（自由入力）" ? data.price_range_text : "",
                          })
                        }
                        style={inputStyle}
                      >
                        <option value="">選択してください</option>
                        {PRICE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>

                    {showPriceText && (
                      <div style={{ marginTop: 10 }}>
                        <Field label="価格帯（自由入力）" required>
                          <input
                            value={data.price_range_text}
                            onChange={(e) => setData({ ...data, price_range_text: e.target.value })}
                            placeholder="例）10万円〜30万円 / 1台あたり20万円 など"
                            style={inputStyle}
                          />
                        </Field>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

                    <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={onBack}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "戻る"}
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#1d4ed8",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>

            <button
  onClick={onList}
  disabled={saving}
  style={{
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#64748b",
    color: "#fff",
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    transition: "background 0.2s ease",
  }}
>
  一覧
</button>

            <button
              onClick={onNext}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "次へ"}
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, color: "#475569" }}>
            ステータス：{" "}
            <span style={{ fontWeight: 900, color: canComplete ? "#dc2626" : "#0b1f3b" }}>
              {statusLabel}
            </span>
          </div>

          {!canComplete && (
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 800 }}>
              ※ 必須項目が未入力のため「未完了」になります
            </div>
          )}
        </>
      )}
    </MerchantLayout>
  );
}

/** ✅ Form1-style field label (red “（必須）”) */
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>
      {children}
    </div>
  );
}

/** ✅ Shared styles (match Form1) */
const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};



function SuffixInput({
  value,
  onChange,
  placeholder,
  suffix,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suffix: string;
}) {
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          ...inputStyle,
          paddingRight: 90,
        }}
      />
      <span
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#475569",
          fontSize: 14,
          fontWeight: 700,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {suffix}
      </span>
    </div>
  );
}

function Pill({ text, active }: { text: string; active?: boolean }) {
  return (
    <div
      style={{
        background: active ? "#1d4ed8" : "#eef2ff",
        border: active ? "1px solid #1d4ed8" : "1px solid #c7d2fe",
        color: active ? "#fff" : "#1e3a8a",
        fontWeight: 900,
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 999,
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );
}

function Mark({ v }: { v: string }) {
  const isOk = v === "○";
  return (
    <div
      style={{
        textAlign: "center",
        fontWeight: 1000,
        color: isOk ? "#166534" : "#b91c1c",
        background: isOk ? "#ecfdf5" : "#fff1f2",
        border: `1px solid ${isOk ? "#bbf7d0" : "#fecdd3"}`,
        padding: "6px 0",
        borderRadius: 10,
      }}
    >
      {v}
    </div>
  );
}

function RowBlock({
  title,
  industryMarks,
  left,
  mid,
  right,
}: {
  title: string;
  industryMarks: [string, string, string, string];
  left: React.ReactNode;
  mid: React.ReactNode;
  right: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 12,
        background: "#ffffff",
      }}
    >
      <div style={{ fontWeight: 1000, marginBottom: 10, color: "#0f172a" }}>{title}</div>

      

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
          gap: 20,
          alignItems: "start",
        }}
      >
        {left}
        {mid}
        {right}
      </div>
    </div>
  );
}
